import React, { useState } from 'react';
import { IconCheck, IconMail, IconArrowRight } from '@tabler/icons-react';

const Pricing = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    leagueName: '',
    estimatedParticipants: '',
    frequency: '',
    customBrandingNeeds: '',
    additionalDetails: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Send email via Firebase Cloud Function or your email service
      const response = await fetch('https://us-central1-southern-list-468215-m7.cloudfunctions.net/sendEnterpriseInquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          organization: '',
          leagueName: '',
          estimatedParticipants: '',
          frequency: '',
          customBrandingNeeds: '',
          additionalDetails: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please email us directly at info@ubsports.com');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="ub-pricing-page">
      <div className="ub-pricing-container">
        {/* Header */}
        <div className="ub-pricing-header">
          <h1>League Management Pricing</h1>
          <p>Choose the plan that fits your league or tournament needs</p>
        </div>

        {/* Pricing Cards */}
        <div className="ub-pricing-grid">
          {/* Free Tier */}
          <div className="ub-pricing-card">
            <div className="ub-pricing-card-header">
              <h2>Free</h2>
              <div className="ub-pricing-amount">
                <span className="ub-currency">$</span>
                <span className="ub-price">0</span>
                <span className="ub-period">/forever</span>
              </div>
              <p className="ub-pricing-description">Perfect for most leagues and tournaments</p>
            </div>

            <div className="ub-pricing-features">
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Up to 120 members</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>All league management features</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Schedule generation</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Standings & statistics</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Team & player management</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>League messaging</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>UBSports branding</span>
              </div>
            </div>

            <button className="ub-pricing-cta ub-secondary">
              Get Started Free
            </button>

            <div className="ub-pricing-note">
              <strong>Ideal for:</strong> Community leagues, recreational tournaments, school sports
            </div>
          </div>

          {/* Premium Tier */}
          <div className="ub-pricing-card ub-featured">
            <div className="ub-popular-badge">Popular</div>
            <div className="ub-pricing-card-header">
              <h2>Premium</h2>
              <div className="ub-pricing-amount">
                <span className="ub-currency">$</span>
                <span className="ub-price">5</span>
                <span className="ub-period">/member/year</span>
              </div>
              <p className="ub-pricing-description">For larger leagues and tournaments</p>
            </div>

            <div className="ub-pricing-features">
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>120+ members</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Everything in Free</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Priority support</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Advanced analytics</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Export data</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Multiple admins</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>UBSports branding</span>
              </div>
            </div>

            <button className="ub-pricing-cta ub-primary">
              Get Started
            </button>

            <div className="ub-pricing-note">
              <strong>Ideal for:</strong> Large tournaments, competitive leagues, multi-division events
            </div>
          </div>

          {/* Enterprise Tier */}
          <div className="ub-pricing-card">
            <div className="ub-pricing-card-header">
              <h2>Enterprise</h2>
              <div className="ub-pricing-amount">
                <span className="ub-price-custom">Custom</span>
              </div>
              <p className="ub-pricing-description">Tailored solutions for your organization</p>
            </div>

            <div className="ub-pricing-features">
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Unlimited participants</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Everything in Premium</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Custom branding & design</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Recurring leagues support</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Dedicated account manager</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>Custom integrations</span>
              </div>
              <div className="ub-feature-item">
                <IconCheck size={20} stroke={2} />
                <span>White-label options</span>
              </div>
            </div>

            <button
              className="ub-pricing-cta ub-secondary"
              onClick={() => setShowContactForm(true)}
            >
              <IconMail size={20} />
              <span>Contact Sales</span>
            </button>

            <div className="ub-pricing-note">
              <strong>Ideal for:</strong> Sports organizations, franchises, national tournaments
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="ub-pricing-faq">
          <h2>Frequently Asked Questions</h2>

          <div className="ub-faq-grid">
            <div className="ub-faq-item">
              <h3>Can I switch plans later?</h3>
              <p>Yes! You can upgrade to Premium at any time if your league grows beyond 120 members.</p>
            </div>

            <div className="ub-faq-item">
              <h3>How is the Premium tier billed?</h3>
              <p>Premium is billed annually at $5 per member. For example, a 200-member league would cost $1,000/year.</p>
            </div>

            <div className="ub-faq-item">
              <h3>What's included in custom branding?</h3>
              <p>Enterprise custom branding includes your logo, color scheme, custom domain, and personalized design elements throughout the platform.</p>
            </div>

            <div className="ub-faq-item">
              <h3>Is there a free trial for Premium?</h3>
              <p>All features are available in the Free tier for leagues under 120 members. You only pay when you need to scale beyond that.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise Contact Form Modal */}
      {showContactForm && (
        <div className="ub-contact-modal" onClick={() => setShowContactForm(false)}>
          <div className="ub-contact-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ub-contact-modal-header">
              <h2>Enterprise Inquiry</h2>
              <button
                className="ub-close-modal-btn"
                onClick={() => setShowContactForm(false)}
              >
                ×
              </button>
            </div>

            {submitSuccess ? (
              <div className="ub-submit-success">
                <IconCheck size={64} stroke={2} />
                <h3>Thank you for your inquiry!</h3>
                <p>Our team will review your request and get back to you within 1-2 business days.</p>
                <button
                  className="ub-btn-close-success"
                  onClick={() => {
                    setSubmitSuccess(false);
                    setShowContactForm(false);
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="ub-contact-form">
                <div className="ub-form-row">
                  <div className="ub-form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="ub-form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="ub-form-group">
                  <label htmlFor="organization">Organization</label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="ub-form-row">
                  <div className="ub-form-group">
                    <label htmlFor="leagueName">League/Tournament Name *</label>
                    <input
                      type="text"
                      id="leagueName"
                      name="leagueName"
                      value={formData.leagueName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="ub-form-group">
                    <label htmlFor="estimatedParticipants">Estimated Participants *</label>
                    <input
                      type="number"
                      id="estimatedParticipants"
                      name="estimatedParticipants"
                      value={formData.estimatedParticipants}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="ub-form-group">
                  <label htmlFor="frequency">Event Frequency *</label>
                  <select
                    id="frequency"
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select frequency</option>
                    <option value="one-time">One-time event</option>
                    <option value="annual">Annual</option>
                    <option value="seasonal">Seasonal (2-4 times/year)</option>
                    <option value="ongoing">Ongoing/Year-round</option>
                  </select>
                </div>

                <div className="ub-form-group">
                  <label htmlFor="customBrandingNeeds">Custom Branding Needs</label>
                  <textarea
                    id="customBrandingNeeds"
                    name="customBrandingNeeds"
                    value={formData.customBrandingNeeds}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Describe your branding requirements..."
                  />
                </div>

                <div className="ub-form-group">
                  <label htmlFor="additionalDetails">Additional Details</label>
                  <textarea
                    id="additionalDetails"
                    name="additionalDetails"
                    value={formData.additionalDetails}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Tell us more about your needs..."
                  />
                </div>

                <div className="ub-form-actions">
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="ub-btn-cancel"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ub-btn-submit"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Inquiry'}
                    <IconArrowRight size={20} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default Pricing;
